import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useLeadDetail } from '../../hooks/useLeadDetail';
import { useISRLeadMutation } from '../../hooks/useISRLeadMutation';
import type {
  CampaignMetadata,
  ISRUpdateDiff,
  ISRUpdateSnapshot,
  LeadCardData,
  MappedLeadData,
} from '../../types';
import { ISRWorkspaceDrawer } from './ISRWorkspace';
import type { ISRWorkspaceActor } from './ISRWorkspace';
import { ISRWorkspaceDrawerShell } from './ISRWorkspaceDrawerShell';
import { isLeadEnrichedForISR, getPreferredStatus } from '../../utils/statusMapping';
import { timingLog } from '../../utils/timingLog';
import { queryKeys } from '../../services/query/queryKeys';

export interface ISRWorkspaceControllerProps {
  open: boolean;
  lead: LeadCardData | null;
  campaign?: CampaignMetadata | null;
  userEmail?: string | null;
  currentUser: ISRWorkspaceActor;
  onClose: () => void;
  onOpenFullLead?: () => void;
  previewMode?: boolean;
  /** Navigate between leads without closing the drawer. */
  onPreviousLead?: () => void;
  hasNextLead?: boolean;
  onNextLead?: () => void;
  hasPreviousLead?: boolean;
  leadPosition?: number;
  totalLeads?: number;
  /** Opens the Report Generator locked to this lead (white "+Report"). */
  onGenerateReport?: (leadId: string, campaignId: string) => void;
}

const isMappedLead = (lead: LeadCardData | null): lead is MappedLeadData =>
  Boolean(lead && 'rawOriginalData' in lead && 'outreachSequences' in lead);

const WorkspaceStateSurface: React.FC<{
  title: string;
  message: string;
  icon: string;
  tone?: 'neutral' | 'error';
  onRetry?: () => void;
}> = ({ title, message, icon, tone = 'neutral', onRetry }) => (
  <div className="flex h-full min-h-0 flex-col bg-[#0a0b0e] text-white">
    <header className="border-b border-white/[0.07] px-5 py-5 pr-28">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/40">SDR workspace</p>
      <h1 id="isr-workspace-title" className="mt-1 text-lg font-semibold">{title}</h1>
    </header>
    <div className="flex min-h-0 flex-1 items-center justify-center p-6">
      <div className="max-w-md p-7 text-center">
        <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-2xl ${tone === 'error' ? 'bg-rose-400/10 text-rose-200' : 'bg-cyan-400/10 text-cyan-200'}`}>
          <Icon icon={icon} className={tone === 'neutral' && !onRetry ? 'h-6 w-6 animate-pulse motion-reduce:animate-none' : 'h-6 w-6'} aria-hidden="true" />
        </span>
        <p className="mt-4 text-[14px] font-medium text-white/90">{title}</p>
        <p className="mt-2 text-[13px] leading-6 text-white/50">{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-5 inline-flex h-9 items-center gap-2 rounded-lg border border-white/15 px-3.5 text-[13px] font-medium text-white/75 hover:border-white/35 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/45"
          >
            <Icon icon="bx:refresh" className="h-4 w-4" aria-hidden="true" />
            Try again
          </button>
        )}
      </div>
    </div>
  </div>
);

/**
 * Hydrates a list-row lead before exposing any mutation controls, then owns the
 * authenticated update mutation. Keeping this boundary outside the generic
 * drawer prevents a previous lead from flashing while the next one loads.
 */
export const ISRWorkspaceController: React.FC<ISRWorkspaceControllerProps> = ({
  open,
  lead,
  campaign,
  userEmail,
  currentUser,
  onClose,
  onOpenFullLead,
  previewMode = false,
  onPreviousLead,
  hasNextLead = false,
  onNextLead,
  hasPreviousLead = false,
  leadPosition,
  totalLeads,
  onGenerateReport,
}) => {
  const campaignId = open && lead ? String(lead.campaignId || '') : '';
  const lvl2Id = open && lead ? String(lead.id || '') : '';
  const canUsePreviewSeed = previewMode && isMappedLead(lead);
  const shouldHydrate = open && !canUsePreviewSeed;
  const hasLoadIdentity = Boolean(campaignId && lvl2Id && userEmail);
  const detailQuery = useLeadDetail(
    shouldHydrate ? campaignId : '',
    shouldHydrate ? lvl2Id : '',
    shouldHydrate ? String(userEmail || '') : '',
  );
  const hydratedLead = (canUsePreviewSeed ? lead : detailQuery.data) as MappedLeadData | null | undefined;
  const queryClient = useQueryClient();

  // Logs the "drawer open" moment and whether the detail cache already held
  // data for this lead (fast paint from persisted cache) or is cold (must
  // wait for the n8n-proxy round-trip). The mountT0Ref stores the timestamp so
  // the hydrating→ready transition can compute elapsed time.
  const hydratingRef = useRef(false);
  const mountT0Ref = useRef<number | null>(null);
  useEffect(() => {
    if (!shouldHydrate || !hasLoadIdentity) return;
    const t0 = performance.now();
    mountT0Ref.current = t0;
    // Inspect the cache BEFORE the query's own fetch resolves: if data is
    // already there, the first paint will be from cache (fast); otherwise the
    // user sees the skeleton until the network call completes.
    const cached = queryClient.getQueryData(queryKeys.leads.detail(campaignId, lvl2Id));
    const cacheState = cached ? 'hit' : 'miss';
    timingLog('init', 'controller mount', {
      campaign: campaignId,
      lead: lvl2Id,
      cache: cacheState,
      queryStatus: detailQuery.status,
      fetchStatus: detailQuery.fetchStatus,
    });
    hydratingRef.current = !cached;
  }, [shouldHydrate, hasLoadIdentity, campaignId, lvl2Id, queryClient, detailQuery.status, detailQuery.fetchStatus]);

  // Hydrating → Ready transition: fires once when hydratedLead goes from
  // null/undefined to a real MappedLeadData.
  const wasHydratedRef = useRef(false);
  useEffect(() => {
    if (!shouldHydrate || !hydratedLead) return;
    if (wasHydratedRef.current) return; // already logged for this lead
    wasHydratedRef.current = true;
    const mountT0 = mountT0Ref.current;
    const sinceMount = mountT0 ? performance.now() - mountT0 : null;
    timingLog('init', 'hydrating→ready', {
      campaign: campaignId,
      lead: lvl2Id,
      sinceMountMs: sinceMount,
      wasCached: !hydratingRef.current,
    });
  }, [shouldHydrate, hydratedLead, campaignId, lvl2Id]);

  // Reset the hydrated flag on lead change so the transition logs again.
  useEffect(() => {
    wasHydratedRef.current = false;
  }, [campaignId, lvl2Id]);

  // ── Status reconciliation: when the detail query resolves, prefer the list
  // cache's status (which reflects optimistic updates from the ISR workspace)
  // over the server's response. Matches the pattern in
  // src/routes/campaigns/$campaignId/index.tsx:112-128 where getPreferredStatus
  // prevents the server from regressing an optimistically-applied status.
  useEffect(() => {
    if (!hydratedLead || !lead) return;
    const listStatus = lead.status;
    if (listStatus && hydratedLead.status !== listStatus) {
      const preferred = getPreferredStatus(listStatus, hydratedLead.status);
      if (preferred !== hydratedLead.status) {
        timingLog('init', 'status reconciled', {
          campaign: campaignId,
          lead: lvl2Id,
          fromServer: hydratedLead.status,
          fromList: listStatus,
          resolved: preferred,
        });
        queryClient.setQueryData(
          queryKeys.leads.detail(campaignId, lvl2Id),
          (old: any) => old ? { ...old, status: preferred, salesSubstatus: hydratedLead.salesSubstatus } : old,
        );
      }
    }
  }, [hydratedLead, lead?.status, campaignId, lvl2Id, queryClient]);

  const mutation = useISRLeadMutation({
    campaignId,
    lvl2Id: hydratedLead?.id ?? lvl2Id,
    userEmail,
    userId: currentUser.id,
    userName: currentUser.name,
    lead: hydratedLead,
  });

  useEffect(() => {
    mutation.reset();
  }, [campaignId, lvl2Id]);

  const mutationError = useMemo(() => {
    if (!mutation.error) return null;
    return mutation.error instanceof Error ? mutation.error.message : 'Could not save SDR changes.';
  }, [mutation.error]);

  const handleSave = useCallback(async (snapshot: ISRUpdateSnapshot, diff?: { changed_fields?: string[]; previous_values?: Record<string, string | null>; updated?: ISRUpdateDiff }) => {
    const changedFields = diff?.changed_fields;
    timingLog('mutation', 'handleSave start', {
      campaign: campaignId,
      lead: lvl2Id,
      changedFields: changedFields?.length ?? 0,
      hasCampaignOnly: changedFields?.length && changedFields.every((f) => ['lead_status', 'lead_substatus', 'notes'].includes(f)),
    });
    const t0 = performance.now();
    // A change is "campaign-owned" when it touches ONLY fields that
    // update_campaign persists atomically WITHOUT the canonical lvl1
    // person/company records: status, substatus, and/or notes. Matches the
    // mutation hook's own classification so the workspace never blocks a
    // notes-only or sales-only save here before the mutation runs.
    const hasCampaignOwnedFields = !!changedFields?.length &&
      changedFields.every((field) => {
        const normalized = field.trim().toLowerCase();
        return normalized === 'lead_status' || normalized === 'lead_substatus' || normalized === 'notes';
      });
    if (!mutation.canMutate && !hasCampaignOwnedFields) {
      // Surface a precise reason instead of failing silently — nothing was
      // sent to the proxy, so the user must see why.
      timingLog('mutation', 'handleSave blocked', {
        campaign: campaignId,
        lead: lvl2Id,
        durationMs: performance.now() - t0,
        reason: 'missing canonical person/company record',
      });
      throw new Error('This lead is missing its canonical person or company record, so contact fields cannot be saved directly. Person edits need the hydrated lvl1 record; company edits are submitted as approval suggestions.');
    }
    const idempotencyKey = globalThis.crypto?.randomUUID?.()
      || `isr-${campaignId}-${lvl2Id}-${Date.now()}`;
    try {
      await mutation.mutateAsync({
        snapshot,
        metadata: {
          expected_version: hydratedLead?.updatedAt || null,
          idempotency_key: idempotencyKey,
          changed_fields: diff?.changed_fields,
          previous_values: diff?.previous_values,
          updated: diff?.updated,
        },
      });
      timingLog('mutation', 'handleSave complete', {
        campaign: campaignId,
        lead: lvl2Id,
        durationMs: performance.now() - t0,
        changedFields: changedFields.length,
      });
    } catch (error) {
      timingLog('mutation', 'handleSave error', {
        campaign: campaignId,
        lead: lvl2Id,
        durationMs: performance.now() - t0,
        error: (error as Error)?.message,
      });
      throw error;
    }
  }, [campaignId, hydratedLead?.updatedAt, lvl2Id, mutation.canMutate, mutation.mutateAsync]);

  if (!open || !lead) return null;

  // The ISR workspace is only meaningful for enriched leads — a NEW LEAD or
  // ENRICHING lead has no briefing, sequence, or activity to work with.
  if (!isLeadEnrichedForISR(lead.status)) {
    return (
      <ISRWorkspaceDrawerShell open onClose={onClose}>
        <WorkspaceStateSurface
          title="SDR workspace not available yet"
          message="This lead hasn't been enriched. Enrich it first — the briefing, sequence and activity views need that data."
          icon="bx:lock-alt"
        />
      </ISRWorkspaceDrawerShell>
    );
  }

  if (shouldHydrate && !hasLoadIdentity) {
    return (
      <ISRWorkspaceDrawerShell open onClose={onClose}>
        <WorkspaceStateSurface
          title="Lead workspace unavailable"
          message="The authenticated user, campaign, or lead identifier is missing. No information was changed."
          icon="bx:error-circle"
          tone="error"
        />
      </ISRWorkspaceDrawerShell>
    );
  }

  // While the full record hydrates, render the workspace from the known
  // list-row lead (name, role, company, status, contact basics) with
  // skeletons in the awaiting sections — no blank blocking screen.
  const isHydrating = shouldHydrate && !hydratedLead && (detailQuery.isPending || detailQuery.isFetching);

  if (isHydrating) {
    return (
      <ISRWorkspaceDrawer
        key={`${campaignId}:${lvl2Id}`}
        open
        onClose={onClose}
        lead={lead as unknown as MappedLeadData}
        campaign={campaign || undefined}
        editable={false}
        saving={false}
        currentUser={currentUser}
        onSaveSnapshot={() => undefined}
        onOpenFullLead={onOpenFullLead}
        onPreviousLead={onPreviousLead}
        onNextLead={onNextLead}
        hasPreviousLead={hasPreviousLead}
        hasNextLead={hasNextLead}
        leadPosition={leadPosition}
        totalLeads={totalLeads}
        onGenerateReport={onGenerateReport}
        hydrating
      />
    );
  }

  if (!hydratedLead) {
    return (
      <ISRWorkspaceDrawerShell open onClose={onClose}>
        <WorkspaceStateSurface
          title="Lead workspace unavailable"
          message="The complete lead record could not be loaded. No information was changed."
          icon="bx:error-circle"
          tone="error"
          onRetry={() => { void detailQuery.refetch(); }}
        />
      </ISRWorkspaceDrawerShell>
    );
  }

  return (
    <ISRWorkspaceDrawer
      key={`${campaignId}:${lvl2Id}`}
      open
      onClose={onClose}
      // Merge statuses by ADVANCEMENT, not by source priority: the list row
      // (carrier of optimistic updates) and the hydrated detail may disagree
      // inside the n8n→DB sync window, and blindly preferring one side could
      // regress the user's just-applied status. getPreferredStatus keeps the
      // most advanced known status — same pattern as useLeadDetail.
      lead={hydratedLead
        ? { ...hydratedLead, status: getPreferredStatus(lead.status, hydratedLead.status) || hydratedLead.status }
        : hydratedLead}
      campaign={campaign || undefined}
      editable={!previewMode && mutation.canMutate}
      salesEditable={!previewMode && mutation.canChangeSalesState}
      saving={mutation.isPending}
      saveError={mutationError || (!previewMode && !mutation.canMutate
        ? 'Saving is unavailable because the canonical person or company record is missing.'
        : null)}
      currentUser={currentUser}
      onSaveSnapshot={handleSave}
      onOpenFullLead={onOpenFullLead}
      onPreviousLead={onPreviousLead}
      onNextLead={onNextLead}
      hasPreviousLead={hasPreviousLead}
      hasNextLead={hasNextLead}
      leadPosition={leadPosition}
      totalLeads={totalLeads}
      onGenerateReport={onGenerateReport}
    />
  );
};

export default ISRWorkspaceController;
